// Authored, rigged Aren Vale (assets/blender/aren_build.py -> public/models/aren.glb), driven by AnimationMixer.
// Opt-in preview (title "Hero model" setting or ?hero=gltf). The procedural rig stays the default + fallback.
// Animation is presentation only: it reads Sim state and never changes gameplay timing or damage.
//
// GLTF-01B (P1 art review of d01aff0):
//  - readability by value, not glow: lifted toon ramp + restrained view-angle rim (gold in Surge)
//  - upper/lower body layering: legs keep locomotion while the torso holds the aim/beam pose
//  - hips twist toward the movement direction (clamped) with backpedal when moving away from the aim
//  - pivot one-shot on sharp direction reversals
import * as THREE from 'three/webgpu';
import { GLTFLoader, type GLTF } from 'three/addons/loaders/GLTFLoader.js';
import { clone as cloneSkinned } from 'three/addons/utils/SkeletonUtils.js';
import { abs, color, dot, float, mix, normalLocal, normalView, oneMinus, positionLocal, positionViewDirection, positionWorld, pow, time, uniform, vertexColor } from 'three/tsl';
import { RenderStyle } from './toon';
import { ANIME, ANIME_INK, animeHairBand, animeHeroColor, animeRim, animeSpec } from './animeLook';
import { surgeForm } from '../core/surgeForms'; // SURGE FORMS (owner 2026-11-03): hair stage / bulk / tints per form
import { PACE } from '../core/mods';
import { BASE_MOVE } from '../core/skillStats';
import { STANCES, StanceLayer, stanceFromParams, type Side } from './heroStance';

export interface HeroDrive {
  /** Rush Flurry: bumps once per strike event; strikeYaw = world yaw toward the struck foe */
  strike?: number; strikeYaw?: number;
  /** Spirit Bolt: bumps once per volley; castYaw = world yaw toward the target */
  cast?: number; castYaw?: number;
  /** Pulse Nova: bumps once per release (squat + two-arm outward push, full body) */
  nova?: number;
  speed: number; firing: boolean; charging: boolean; transformed: boolean; hurt: boolean; dead: boolean;
  /** current Surge level aura colour */
  surgeColor?: number;
  /** Resonance Surge: smoothed level 0..3 (drives morphs/stance/ring) and the integer level (transformation trigger) */
  surgeTier?: number; tier?: number;
  /** level hair colour (authored model) */
  hairColor?: THREE.Color;
  /** SURGE FORMS: the form's eye-glow tint (the glow material: eyes + chest crystal) */
  eyeColor?: THREE.Color;
  /** facing yaw (rad) — aim/beam direction while firing, else input direction */
  faceYaw: number;
  /** movement yaw (rad) from velocity; ignored when speed is ~0 */
  moveYaw: number;
  /** signed yaw change requested this frame before smoothing (rad) — large values = reversal */
  turn: number;
  /** where the upper body should aim (beam direction) while firing/charging; legs keep facing travel */
  aimYaw?: number;
}

const _footW = new THREE.Vector3();
const LOCO = ['idle', 'idle_power', 'walk', 'run'];
/** Locomotion blend + clip-rate tuning (m/s). walkFull: idle->walk done; runFrom..runFull: walk->run crossfade;
 *  walkRef / runRef: speed at which the walk / run clip plays at its authored rate (timeScale = speed / ref).
 *  LEGACY was tuned for the original 8.5 m/s run: at the paced 7.2 m/s (PACE 0.85) the hero was only ~75 % into the run
 *  with a 1.6x walk mixed in under it (feet shuffle between two cadences). PACED (owner 2026-11-02): the same thresholds
 *  x PACE, with the run fully in by 6.05 m/s so the 0.9x beam slow (6.5 m/s) is still a full run; the run clip plays at
 *  its authored rate at the paced run speed (BASE_MOVE x PACE = 7.2 m/s), the cadence the owner approved at 8.5. */
export interface LocoTune { walkFull: number; runFrom: number; runFull: number; walkRef: number; runRef: number }
export const LOCO_LEGACY: LocoTune = { walkFull: 4, runFrom: 5, runFull: 8, walkRef: 4.5, runRef: 8.5 };
export const LOCO_PACED: LocoTune = { walkFull: 4 * PACE, runFrom: 5 * PACE, runFull: 6.05, walkRef: 4.5 * PACE, runRef: BASE_MOVE * PACE };
const UPPER_POSE = ['beam', 'aim'];
/** Rush Flurry combo order (punches upper-body only so the legs keep walking; kicks are full-body) */
const STRIKE_SEQ = ['strike_jab', 'strike_cross', 'strike_kick_r', 'strike_cross', 'strike_jab', 'strike_kick_l'];
const PUNCHES = ['strike_jab', 'strike_cross', 'cast_r', 'cast_l'];
const LOWER_BONES = /^(hips|thigh|shin|foot|toe)/;
/** LEVEL-UP MOMENT: the Surge clip up to just past the battle-cry key (frame 19 = ROAR in aren_build.py at 30 fps) */
const LVL_CHARGE_FRAMES = 21;

let heroRamp: THREE.DataTexture | null = null;
/** Hero-only toon ramp: shadows lifted (P1: fix the dark silhouette through material value). */
function heroGradient(): THREE.DataTexture {
  if (heroRamp) return heroRamp;
  const d = new Uint8Array([138, 138, 150, 255, 204, 204, 210, 255, 255, 255, 255, 255]);
  heroRamp = new THREE.DataTexture(d, 3, 1, THREE.RGBAFormat);
  heroRamp.minFilter = THREE.NearestFilter; heroRamp.magFilter = THREE.NearestFilter;
  heroRamp.generateMipmaps = false; heroRamp.needsUpdate = true;
  ANIME.registerRamp(heroRamp, [112, 226, 255]); // ?look=anime: 2-tone hero ramp (clean shadow shape, bright lit side)
  return heroRamp;
}

function splitClip(clip: THREE.AnimationClip, lower: boolean): THREE.AnimationClip {
  const tracks = clip.tracks.filter((t) => LOWER_BONES.test(t.name.split('.')[0]) === lower);
  return new THREE.AnimationClip(`${clip.name}_${lower ? 'lo' : 'up'}`, clip.duration, tracks);
}

const GLB_CACHE = new Map<string, Promise<GLTF>>();
/** LOAD-PERF: resolves once every hero GLB requested so far is fetched + parsed (the warm-up waits for the ghost pools) */
export function heroGlbsSettled(): Promise<unknown> { return Promise.allSettled([...GLB_CACHE.values()]); }

export class GltfHero {
  readonly root = new THREE.Group();
  ready = false;
  /** draw calls contributed by the inked shell (for P1's separate outline measurement) */
  outlineMeshes: THREE.Object3D[] = [];
  private mixer: THREE.AnimationMixer | null = null;
  private act = new Map<string, THREE.AnimationAction>();
  private glowMat: THREE.MeshBasicNodeMaterial | null = null;
  private rimColor = uniform(new THREE.Color(0x9fdcff));
  private rimGain = uniform(0.32);
  /** ?aura=v3: broad aura back-light (the hero picks up the aura colour around his silhouette); 0 otherwise */
  private auraLit = uniform(0);
  private static readonly AURA_V3 = typeof location !== 'undefined' && new URLSearchParams(location.search).get('aura') !== 'old'; // owner 2026-11-02: 'Make the new aura the standard' (?aura=old = the flipbook aura)
  // Resonance Surge (v2 model): hair tint/glow, morph targets, ring bones
  private hairTint = uniform(new THREE.Color(0x1c1f2a)); private hairK = uniform(0); private hairGlow = uniform(0);
  /** hair glow colour: the tint itself, except VII Abyssal Singularity (black hair, red glow) */
  private hairGlowCol = uniform(new THREE.Color(0x1c1f2a));
  /** SURGE FORMS: smoothed shape-key weights (hair stage cross-fades between forms, bulk eases to the form's value) */
  private hairW = { rise: 0, sweep: 0, crest: 0 }; private bulkW = 0;
  private morphMeshes: THREE.Mesh[] = [];
  private ring: THREE.Bone | null = null; private ringSegs: THREE.Bone[] = [];
  private lastTier = 0;
  /** true when the loaded model carries the Surge morph targets (v2) */
  hasSurge = false;
  /** Pass B artlab switches: emission (rim, hair glow, core boost) on/off; force the powered-stance weight; unlit albedo view */
  emissionOn = true; stanceOverride: number | null = null;
  /** walk/run blend tuning (the animation lab sets LOCO_LEGACY for before/after previews) */
  loco: LocoTune = LOCO_PACED;
  private albedoMats = new Map<THREE.Mesh, THREE.Material>();
  private aimTwist = 0;
  private lastStrike = 0; private lastCast = 0; private castSide = 0; private lastNova = 0; private strikeIdx = 0; private strikeCd = 0; private strikeT = 0; private strikeKick = false; private strikeYaw = 0;
  private feet: THREE.Bone[] = []; private restFootY = NaN; private plant = 0;
  private hips: THREE.Bone | null = null; private spine: THREE.Bone | null = null; private chest: THREE.Bone | null = null;
  /** QA counters (herocheck) */
  stats = { pivots: 0, maxTwist: 0, backFrames: 0, frames: 0 };
  private twist = 0; private back = 0; private pivotCd = 0;
  private wasTransformed = false; private wasHurt = false; private wasDead = false;
  private q = new THREE.Quaternion(); private pq = new THREE.Quaternion(); private axis = new THREE.Vector3();
  /** world units per model metre (model is ~1.86 m tall -> ~2.0 world m, human scale against the props) */
  static readonly SCALE = 1.07;
  /** Spirit Bolt arm-thrust cast (built, awaiting owner approval) */
  static CAST_ANIM = new URLSearchParams(typeof location !== 'undefined' ? location.search : '').has('castanim');
  /** FIGHT STANCE (owner 2026-11-03, candidates a|b|c|d via ?idle=, awaiting owner approval): the resting fight stance layered
   *  over the idle clip (src/render/heroStance.ts). 'current' = the plain idle. Per instance so the lab shows several. */
  static STANCE = stanceFromParams(new URLSearchParams(typeof location !== 'undefined' ? location.search : ''));
  stanceKey = GltfHero.STANCE;
  private stanceLayer: StanceLayer | null = null;
  private handGlow: Record<Side, THREE.Mesh | null> = { L: null, R: null };
  private glowT = 0;

  constructor(private style: RenderStyle) {}

  private toon(spec = false): THREE.MeshToonNodeMaterial {
    const m = new THREE.MeshToonNodeMaterial({ gradientMap: heroGradient() });
    m.vertexColors = true;
    // restrained fresnel rim: thin, low-gain, view-dependent — separates the silhouette from grass/char
    const facing = normalView.dot(positionViewDirection).clamp(0, 1);
    const rim = float(1).sub(facing).pow(3.5).mul(this.rimGain).add(float(1).sub(facing).pow(1.6).mul(this.auraLit));
    const em = this.rimColor.mul(rim);
    m.emissiveNode = em;
    // ?look=anime (P1): indigo shadow band + controlled directional rim; armour also gets inner ink edges and a hard highlight
    if (ANIME.enabled) {
      m.vertexColors = false; m.colorNode = animeHeroColor(vertexColor(), spec);
      m.emissiveNode = em.add(animeRim(0.6)).add(spec ? animeSpec(0.5) : float(0));
    }
    return m;
  }

  private hairToon(): THREE.MeshToonNodeMaterial {
    const m = this.toon();
    m.vertexColors = false; // the colour node already samples the vertex colour (otherwise the tint gets multiplied by it)
    const hairCol = mix(vertexColor(), this.hairTint, this.hairK);
    m.colorNode = hairCol;
    const facing = normalView.dot(positionViewDirection).clamp(0, 1);
    const em = this.rimColor.mul(float(1).sub(facing).pow(3.5).mul(this.rimGain).add(float(1).sub(facing).pow(1.6).mul(this.auraLit))).add(this.hairGlowCol.mul(this.hairGlow));
    m.emissiveNode = em;
    if (ANIME.enabled) { // ?look=anime: indigo shadow band, rim + hair halo band
      m.emissiveNode = em.add(animeRim(0.6)).add(animeHairBand(hairCol, 0.6));
      m.colorNode = animeHeroColor(hairCol, false);
    }
    return m;
  }

  /** LOAD-PERF: every copy of the hero (the hero, dodge afterimage, Mirage Lure / Split Self / Phase Rend ghosts — up to 18)
   *  used to fetch and parse its own GLB on first use, mid-run. Now each file is fetched + parsed once (fetchGlb, started
   *  early by src/app/preload.ts) and every load() works on a skinned clone of that pristine scene (shared geometry). */
  static fetchGlb(url: string): Promise<GLTF> {
    let p = GLB_CACHE.get(url);
    if (!p) { p = new GLTFLoader().loadAsync(url); GLB_CACHE.set(url, p); p.catch(() => GLB_CACHE.delete(url)); }
    return p;
  }

  async load(url: string): Promise<void> {
    const src = await GltfHero.fetchGlb(url);
    const gltf = { scene: cloneSkinned(src.scene), animations: src.animations };
    const cel = this.style === 'cel';
    const body = cel ? this.toon() : new THREE.MeshStandardNodeMaterial({ roughness: 0.62, vertexColors: true });
    const armor = cel ? this.toon(true) : new THREE.MeshStandardNodeMaterial({ roughness: 0.35, metalness: 0.25, vertexColors: true });
    const glow = new THREE.MeshBasicNodeMaterial({ color: new THREE.Color(0x63e0d7).multiplyScalar(2.2) });
    const hairM = cel ? this.hairToon() : new THREE.MeshStandardNodeMaterial({ roughness: 0.5, vertexColors: true });
    this.glowMat = glow;
    const outlineMat = new THREE.MeshBasicNodeMaterial({ color: 0x0b0612, side: THREE.BackSide });
    if (ANIME.enabled) outlineMat.colorNode = mix(color(0x0b0612), color(ANIME_INK), ANIME.k); // ?look=anime: navy ink
    outlineMat.positionNode = positionLocal.add(normalLocal.mul(ANIME.enabled ? ANIME.heroInk : float(0.01))); // ?look=anime: ~2 px at any camera distance
    const extra: Array<[THREE.Object3D, THREE.Object3D]> = [];
    gltf.scene.traverse((o) => {
      const b = o as THREE.Bone;
      if (b.isBone) {
        if (/^foot\.?[LR]$/.test(b.name)) this.feet.push(b);
        if (b.name === 'hips') this.hips = b; else if (b.name === 'spine') this.spine = b; else if (b.name === 'chest') this.chest = b;
        else if (b.name === 'ring') this.ring = b; else if (b.name.startsWith('ringseg')) this.ringSegs.push(b);
      }
      const m = o as THREE.SkinnedMesh;
      if (!m.isMesh) return;
      const name = (m.material as THREE.Material).name;
      m.material = name === 'glow' ? glow : name === 'armor' ? armor : name === 'hair' ? hairM : body;
      if (m.morphTargetDictionary && 'bulk' in m.morphTargetDictionary) { this.morphMeshes.push(m); this.hasSurge = true; }
      m.castShadow = true; m.frustumCulled = false;
      // no ink shell on the faceted hair: its thin blades get swallowed by any back-face shell (they keep the rim light)
      if (cel && name !== 'glow' && name !== 'hair' && m.isSkinnedMesh) {
        const o2 = new THREE.SkinnedMesh(m.geometry, outlineMat);
        o2.bind(m.skeleton, m.bindMatrix); o2.frustumCulled = false; o2.castShadow = false;
        if (m.morphTargetInfluences) o2.morphTargetInfluences = m.morphTargetInfluences; // outline follows the morphs
        extra.push([o2, m.parent!]);
      }
    });
    for (const [o, p] of extra) { p.add(o); this.outlineMeshes.push(o); }
    gltf.scene.scale.setScalar(GltfHero.SCALE);
    this.root.add(gltf.scene);
    const mixer = new THREE.AnimationMixer(gltf.scene);
    this.mixer = mixer;
    const byName = new Map(gltf.animations.map((c) => [c.name, c]));
    const add = (clip: THREE.AnimationClip, loop: boolean, w = 0) => {
      const a = mixer.clipAction(clip);
      if (!loop) { a.setLoop(THREE.LoopOnce, 1); a.clampWhenFinished = clip.name === 'defeat'; }
      else { a.play(); a.setEffectiveWeight(w); }
      this.act.set(clip.name, a);
    };
    // locomotion split into lower (legs/hips) and upper (spine and above) layers
    for (const n of LOCO) {
      const c = byName.get(n); if (!c) continue;
      add(splitClip(c, true), true, n === 'idle' ? 1 : 0);
      add(splitClip(c, false), true, n === 'idle' ? 1 : 0);
    }
    for (const n of UPPER_POSE) { const c = byName.get(n); if (c) add(splitClip(c, false), true, 0); }
    for (const n of PUNCHES) { const c = byName.get(n); if (c) add(splitClip(c, false), false, 0); }
    for (const c of gltf.animations) if (!LOCO.includes(c.name) && !UPPER_POSE.includes(c.name) && !PUNCHES.includes(c.name) && !c.name.startsWith('act_')) add(c, c.name.startsWith('idle'), 0);
    // LEVEL-UP MOMENT (owner 2026-11-02: 'the character clenches his fist and starts … charging up'; ?lupose=1 until the
    // owner approves the lab preview): the gather → tension → battle-cry part of the APPROVED Surge transformation clip
    // (frames 0-21 of 37: crouch, fists clenched at the hips, shoulders shaking, release), played sped up and faded back out
    const surgeClip = byName.get('surge');
    if (surgeClip) { add(THREE.AnimationUtils.subclip(surgeClip, 'lvl_charge', 0, LVL_CHARGE_FRAMES, 30), false, 0); this.act.get('lvl_charge')!.clampWhenFinished = true; }
    this.initActions(mixer, gltf.animations, gltf.scene); // skill action layer (?anims=new), see the block at the end of the class
    for (const b of [this.ring, ...this.ringSegs]) if (b) this.ringRest.push([b, b.position.clone(), b.quaternion.clone(), b.scale.clone()]);
    this.stanceLayer = new StanceLayer(gltf.scene, Math.random()); // (seed only staggers the micro-adjustments between copies)
    this.ready = true;
  }

  /** FIGHT STANCE: the idle clip's share of the mixer's blend, lower and upper body. Mirrors the mixer's own normalisation
   *  (it averages by weight), so the stance fades exactly as the idle does under runs, strikes, the lance, Surge, level-up. */
  private stanceWeights(d: HeroDrive): [number, number] {
    let loI = 0, loO = 0, upI = 0, upO = 0;
    for (const [n, a] of this.act) {
      if (!a.enabled || !a.isScheduled()) continue;
      const w = a.getEffectiveWeight(); if (w <= 1e-4) continue;
      const idle = n.startsWith('idle');
      if (n.endsWith('_lo')) { if (idle) loI += w; else loO += w; }
      else if (n.endsWith('_up')) { if (idle) upI += w; else upO += w; }
      else { loO += w; upO += w; } // full-body one-shots (kicks, nova, surge, flinch, defeat, stop, lvl_charge)
    }
    if (d.dead) return [0, 0];
    const lance = Math.min(1, (this.baseW.get('beam_up') ?? 0) + (this.baseW.get('aim_up') ?? 0));
    return [loI / Math.max(1e-3, loI + loO), (upI / Math.max(1e-3, upI + upO)) * (1 - lance)];
  }

  /** FIGHT STANCE candidate C: a faint energy glow in the low hand (additive sprite on the hand bone; bloom lifts it) */
  private updateHandGlow(dt: number, side: Side | null, w: number, d: HeroDrive): void {
    for (const s of ['L', 'R'] as Side[]) {
      let m = this.handGlow[s];
      if (s !== side) { if (m) m.visible = false; continue; }
      if (!m) {
        const b = this.handB[s]; if (!b) return;
        const mat = new THREE.MeshBasicNodeMaterial({ color: 0x8af4ff, transparent: true, depthWrite: false, blending: THREE.AdditiveBlending, fog: false });
        m = new THREE.Mesh(new THREE.SphereGeometry(0.05, 12, 8), mat); m.position.set(0, 0.08, 0.01); m.renderOrder = 3; m.castShadow = false;
        b.add(m); this.handGlow[s] = m;
      }
      this.glowT += dt;
      const k = w * (this.emissionOn ? 1 : 0), pulse = 0.75 + 0.25 * Math.sin(this.glowT * 5.5);
      m.visible = k > 0.02;
      m.scale.setScalar((0.7 + 0.5 * pulse) * (0.6 + 0.4 * k));
      const mat = m.material as THREE.MeshBasicNodeMaterial; mat.opacity = 0.55 * k * pulse;
      (mat.color as THREE.Color).setHex(d.transformed ? (d.surgeColor ?? 0xffd45e) : 0x8af4ff).multiplyScalar(1.6);
    }
  }

/** Ghost opacity 0..1 (Mirage Lure decoy) */
  readonly ghostAlpha = uniform(0);
  /**
   * Mirage Lure (owner 2026-11-01: 'a copy of me, but more like a transparent ghost'): swap every material for one
   * translucent spectral material — faint cyan body, bright fresnel rim, slow upward scan bands — and drop the ink
   * shells. Call after load().
   */
  makeGhost(): void {
    const m = new THREE.MeshBasicNodeMaterial({ transparent: true, depthWrite: false, side: THREE.FrontSide, fog: false });
    const fres = pow(oneMinus(abs(dot(normalView, positionViewDirection))), float(2.0));
    const scan = positionWorld.y.mul(7).sub(time.mul(3)).sin().mul(0.5).add(0.5).pow(6);
    const flick = time.mul(23).sin().mul(0.06).add(0.94);
    m.colorNode = mix(color(0x2a7cff), color(0xc8f0ff), fres).mul(float(1.15).add(scan.mul(0.5)));
    m.opacityNode = fres.mul(0.6).add(0.38).add(scan.mul(0.2)).min(0.95).mul(flick).mul(this.ghostAlpha);
    this.root.traverse((o) => {
      const mm = o as THREE.Mesh; if (!mm.isMesh) return;
      if (this.outlineMeshes.includes(mm)) { mm.visible = false; return; }
      mm.material = m; mm.castShadow = false; mm.renderOrder = 4;
    });
  }

  /** artlab: show unlit base colour (vertex colour x tint) to check source colours independent of lighting/ramp */
  setAlbedoView(on: boolean): void {
    this.root.traverse((o) => {
      const m = o as THREE.Mesh; if (!m.isMesh || this.outlineMeshes.includes(m)) return;
      if (on && !this.albedoMats.has(m)) {
        this.albedoMats.set(m, m.material as THREE.Material);
        const src = m.material as THREE.MeshToonNodeMaterial;
        const b = new THREE.MeshBasicNodeMaterial(); if (src.colorNode) { b.colorNode = src.colorNode; } else { b.vertexColors = true; b.color.copy((src as unknown as { color: THREE.Color }).color ?? new THREE.Color(1, 1, 1)); }
        m.material = b;
      } else if (!on && this.albedoMats.has(m)) { m.material = this.albedoMats.get(m)!; this.albedoMats.delete(m); }
    });
    for (const o of this.outlineMeshes) o.visible = !on;
  }

  /** LEVEL-UP MOMENT: seconds left of the charge-up action (then it fades back to idle) */
  private lvlT = 0;
  /** LEVEL-UP MOMENT (?lupose=1): fists clench, slight crouch, tension, release — the trimmed Surge power-up segment played
   *  in `dur` seconds (full body; locomotion steps aside like the approved Pulse Nova push). false when the model lacks it. */
  levelUpPose(dur: number): boolean {
    const a = this.act.get('lvl_charge'); if (!a) return false;
    this.oneShot('lvl_charge', 0.04); a.timeScale = a.getClip().duration / Math.max(0.1, dur);
    this.lvlT = dur; this.strikeKick = true; this.strikeT = dur + 0.04; this.strikeCd = dur;
    return true;
  }

  private oneShot(name: string, fade = 0.08, w = 1): void {
    const a = this.act.get(name); if (!a) return;
    a.reset(); a.setEffectiveWeight(w); a.fadeIn(fade); a.play();
  }

  /** rotate a bone about world-up by `ang` (applied after the mixer, before render) */
  private yawBone(b: THREE.Bone | null, ang: number): void {
    if (!b || !b.parent || Math.abs(ang) < 1e-4) return;
    b.parent.getWorldQuaternion(this.pq);
    this.axis.set(0, 1, 0).applyQuaternion(this.pq.invert()).normalize();
    this.q.setFromAxisAngle(this.axis, ang);
    b.quaternion.premultiply(this.q);
    b.updateMatrixWorld(true);
  }

  /** morph targets + hair colour for the current (smoothed) Surge level. SURGE FORMS (owner 2026-11-03): the form the
   *  smoothed level rounds to picks the hair stage (fan = hair_rise, sweep = hair_sweep, crest = hair_crest; the v2 model
   *  folds sweep into rise) and the bulk; the weights ease over ~0.17 s so a form-up morphs rather than pops. The hair
   *  tint / glow and the eye tint come from the view (SurgeVisual blends the form colours). */
  private applySurge(st: number, d: HeroDrive, dt: number): void {
    if (!this.hasSurge) return;
    const set = (k: string, v: number) => { for (const m of this.morphMeshes) { const i = m.morphTargetDictionary![k]; if (i !== undefined) m.morphTargetInfluences![i] = v; } };
    const hasSweep = this.morphMeshes.some((m) => 'hair_sweep' in m.morphTargetDictionary!);
    const f = surgeForm(Math.max(1, Math.round(st))), on = Math.max(0, Math.min(1, st)), k = Math.min(1, dt * 6), W = this.hairW;
    const want = { rise: f?.hair === 'fan' ? 1 : 0, sweep: f?.hair === 'sweep' ? 1 : 0, crest: f?.hair === 'crest' ? 1 : 0 };
    if (!hasSweep) { want.rise = Math.max(want.rise, want.sweep); want.sweep = 0; }
    W.rise += (want.rise * on - W.rise) * k; W.sweep += (want.sweep * on - W.sweep) * k; W.crest += (want.crest * on - W.crest) * k;
    set('hair_rise', W.rise); set('hair_sweep', W.sweep); set('hair_crest', W.crest);
    this.bulkW += ((f?.bulk ?? 0) * on - this.bulkW) * k; set('bulk', this.bulkW);
    const I = f?.intensity ?? 0;
    set('fierce', Math.min(1, on * (0.4 + 0.5 * I) + (d.charging || d.firing ? 0.3 : 0)));
    if (d.hairColor) (this.hairTint.value as THREE.Color).copy(d.hairColor);
    const abyss = f?.motif === 'abyss'; // VII: black hair whose strands glow red (the tint is near-black, so the glow takes the rim red)
    if (abyss) (this.hairGlowCol.value as THREE.Color).setHex(f!.accent); else (this.hairGlowCol.value as THREE.Color).copy(this.hairTint.value as THREE.Color);
    this.hairK.value = on; this.hairGlow.value = !this.emissionOn || st <= 0 ? 0 : (0.05 + 0.1 * I) * on * (abyss ? 3.2 : 1);
  }

  /** Surge III: the segmented ring breaks into an orbital configuration (after the mixer, presentation only) */
  private ringT = 0;
  /** rest transforms: the exporter drops constant channels, so the mixer does not reset these bones every frame */
  private ringRest: Array<[THREE.Bone, THREE.Vector3, THREE.Quaternion, THREE.Vector3]> = [];
  private ringPose(st: number): void {
    if (!this.ring || !this.ringSegs.length) return;
    const k = Math.max(0, Math.min(1, st - 2)), open = Math.min(1, st / 2);
    this.ringT += 0.016 * (0.4 + st);
    this.ring.scale.setScalar(1 + 0.12 * open + 0.3 * k);
    // the mixer rewrites every bone each frame (all clips key the full rig), so these offsets are absolute, not cumulative
    if (k > 0.01) this.ring.rotateZ(this.ringT * 1.4 * k);
    this.ringSegs.forEach((b, i) => {
      const dir = new THREE.Vector3(0, 1, 0).applyQuaternion(b.quaternion);
      b.position.addScaledVector(dir, 0.03 * open + 0.09 * k + 0.02 * k * Math.sin(this.ringT * 3 + i * 1.7));
      if (k > 0.01) b.rotateX(Math.sin(this.ringT * 2 + i) * 0.6 * k);
    });
  }

  update(dt: number, d: HeroDrive): void {
    if (!this.mixer) return;
    const k = Math.min(1, dt * 14);
    const fire = d.firing || d.charging ? 1 : 0;
    // --- lower-body heading: twist hips toward travel, backpedal when moving away from the aim
    let rel = 0;
    if (d.speed > 0.6) { rel = d.moveYaw - d.faceYaw; rel = Math.atan2(Math.sin(rel), Math.cos(rel)); }
    // the body already faces the stick direction (view.ts), so legs never backpedal (owner: 'runs backwards / inconsistent')
    const backT = 0;
    this.back += (backT - this.back) * Math.min(1, dt * 10);
    if (backT) rel = rel - Math.sign(rel) * Math.PI;                 // run backwards, legs face away from travel
    const twistT = Math.max(-0.25, Math.min(0.25, rel)); // small lead into turns only (was ±60°)
    this.twist += (twistT - this.twist) * Math.min(1, dt * 16);

    // Rush Flurry: each strike event plays the next punch/kick of the combo (rate-limited; the flurry ticks faster than a blow reads)
    this.strikeCd = Math.max(0, this.strikeCd - dt); this.strikeT = Math.max(0, this.strikeT - dt);
    if (d.strike !== undefined && d.strike !== this.lastStrike) {
      this.lastStrike = d.strike;
      if (this.strikeCd === 0 && !d.dead) {
        let name = STRIKE_SEQ[this.strikeIdx++ % STRIKE_SEQ.length];
        if (!this.act.has(PUNCHES.includes(name) ? `${name}_up` : name)) name = '';
        if (name) {
          this.strikeKick = !PUNCHES.includes(name);
          // owner 2026-11-01: '3 times faster… a short, quick loop': clips play at 3x so every flurry strike (83 ms apart) shows a blow
          const nm = this.strikeKick ? name : `${name}_up`; this.oneShot(nm, 0.02); this.act.get(nm)!.timeScale = 3;
          this.strikeT = this.strikeKick ? 0.14 : 0.12; this.strikeCd = this.strikeKick ? 0.1 : 0.06;
          this.strikeYaw = d.strikeYaw ?? d.faceYaw;
        }
      }
    }
    // Pulse Nova: squat and push outward with both arms (full body; takes priority over flurry/cast blows)
    if (d.nova !== undefined && d.nova !== this.lastNova) {
      this.lastNova = d.nova;
      if (!d.dead && this.act.has('nova_push')) { this.oneShot('nova_push', 0.03); this.act.get('nova_push')!.timeScale = 1.25; this.strikeKick = true; this.strikeT = 0.58; this.strikeCd = 0.5; this.strikeYaw = d.faceYaw; }
    }
    // Spirit Bolt: arm thrust toward the target, alternating hands (skipped while a flurry blow is playing)
    if (d.cast !== undefined && d.cast !== this.lastCast) {
      this.lastCast = d.cast;
      const nm = this.castSide++ % 2 ? 'cast_l_up' : 'cast_r_up';
      // owner 2026-11-01 approved the Nova push and the 3x flurry; the Spirit Bolt arm thrust is not approved yet (off)
      if (GltfHero.CAST_ANIM && this.strikeT === 0 && !d.dead && this.act.has(nm)) {
        this.oneShot(nm, 0.04); this.act.get(nm)!.timeScale = 1.15;
        this.strikeKick = false; this.strikeT = 0.36; this.strikeCd = 0.06; this.strikeYaw = d.castYaw ?? d.faceYaw;
      }
    }
    if (this.lvlT > 0) { this.strikeYaw = d.faceYaw; this.lvlT -= dt; if (this.lvlT <= 0) this.act.get('lvl_charge')?.fadeOut(0.2); } // LEVEL-UP MOMENT: back to idle
    const strikeW = Math.min(1, this.strikeT / 0.03);
    const L = this.loco;
    const walkW = Math.max(0, Math.min(1, d.speed / L.walkFull)), runW = Math.max(0, Math.min(1, (d.speed - L.runFrom) / (L.runFull - L.runFrom)));
    this.updateActions(dt, d, walkW); // skill action layer: envelopes + how much of the upper/lower body it takes
    // base (smoothed) weights are kept apart from the applied ones so the action layer can scale locomotion without
    // feeding back into the smoothing; with no action playing the scale is 1 and this is the previous behaviour
    const w = (n: string, v: number) => { const a = this.act.get(n); if (!a) return; const cur = this.baseW.get(n) ?? a.getEffectiveWeight(); const nv = cur + (v - cur) * k; this.baseW.set(n, nv); a.setEffectiveWeight(nv * this.layerScale(n)); };
    const st = d.surgeTier ?? (d.transformed ? 1 : 0);
    const pw = this.stanceOverride ?? (this.act.has('idle_power_lo') ? Math.min(1, st * 0.45) : 0); // powered stance grows with the level
    const lw = { idle: (1 - walkW) * (1 - pw), idle_power: (1 - walkW) * pw, walk: walkW * (1 - runW), run: runW };
    for (const n of LOCO) {
      w(`${n}_lo`, lw[n as keyof typeof lw] * (this.strikeKick ? 1 - strikeW * 0.85 : 1));
      // upper body keeps a little locomotion sway under the aim pose so the run doesn't look frozen
      w(`${n}_up`, lw[n as keyof typeof lw] * (1 - fire * 0.82) * (1 - strikeW * 0.85));
    }
    w('beam_up', d.firing ? 1 : 0);
    w('aim_up', d.charging && !d.firing ? 1 : 0);
    const dir = this.back > 0.5 ? -1 : 1;
    for (const part of ['lo', 'up']) {
      const walk = this.act.get(`walk_${part}`), run = this.act.get(`run_${part}`);
      if (walk) walk.timeScale = dir * Math.max(0.6, d.speed / L.walkRef);
      if (run) run.timeScale = dir * Math.max(0.8, d.speed / L.runRef);
    }
    // --- pivot: a fast reversal while moving plays the plant-and-turn
    this.pivotCd = Math.max(0, this.pivotCd - dt);
    if (Math.abs(d.turn) > 2.0 && d.speed > 2.5 && !fire && this.pivotCd === 0) { this.oneShot('stop', 0.05, 0.85); this.pivotCd = 0.35; this.stats.pivots++; }

    const tierI = d.tier ?? (d.transformed ? 1 : 0);
    if ((tierI > this.lastTier || (d.transformed && !this.wasTransformed)) && !this.actionsLive) this.oneShot('surge', 0.06); // action layer plays act_powerup instead
    this.lastTier = tierI;
    this.applySurge(st, d, dt);
    if (d.hurt && !this.wasHurt) this.oneShot(Math.random() < 0.5 ? 'flinch_l' : 'flinch_r');
    if (d.dead && !this.wasDead) this.oneShot('defeat', 0.15);
    // owner 2026-11-02 bug: the defeat clip clamps on its last frame, so the next run started lying down. Alive again → stop it.
    if (!d.dead && this.wasDead) { const da = this.act.get('defeat'); if (da) { da.stop(); da.reset(); } }
    this.wasTransformed = d.transformed; this.wasHurt = d.hurt; this.wasDead = d.dead;
    // SURGE FORMS: the eyes / chest crystal glow in the form's eye tint (falls back to the aura colour)
    if (this.glowMat) { const gc = this.glowMat.color as THREE.Color; if (d.transformed && d.eyeColor) gc.copy(d.eyeColor); else gc.setHex(d.transformed ? (d.surgeColor ?? 0xffd45e) : 0x63e0d7); gc.multiplyScalar((d.charging ? 4 : 2.2) + Math.min(3, st) * 0.4); }
    (this.rimColor.value as THREE.Color).setHex(d.transformed ? (d.surgeColor ?? 0xffd45e) : 0x9fdcff);
    this.rimGain.value = !this.emissionOn ? 0 : d.transformed ? 0.42 + 0.06 * st : 0.32;
    this.auraLit.value = GltfHero.AURA_V3 && this.emissionOn && d.transformed ? 0.3 + 0.3 * (surgeForm(Math.max(1, Math.round(st)))?.intensity ?? 0) : 0; // SURGE FORMS: back-light grows with the form
    for (const [b, p, q, sc] of this.ringRest) { b.position.copy(p); b.quaternion.copy(q); b.scale.copy(sc); }
    this.restoreTorso(); // action layer: undo last frame's post-mixer twists (see restoreTorso)
    this.stanceLayer?.restore(); // FIGHT STANCE: undo last frame's stance offsets (same reason)
    this.mixer.update(dt);
    this.saveTorso();
    this.stanceLayer?.save();
    this.ringPose(st);
    if (this.stanceLayer) { // FIGHT STANCE candidates (?idle=a|b|c|d; default 'current' = no layer)
      const S = STANCES[this.stanceKey]; this.stanceLayer.stance = S && S.pose.length ? S : null;
      const [lo, up] = this.stanceWeights(d);
      this.stanceLayer.apply(dt, lo, up);
      this.updateHandGlow(dt, S?.glow ?? null, this.stanceLayer.up, d);
    }
    this.stats.frames++; if (this.back > 0.5) this.stats.backFrames++; this.stats.maxTwist = Math.max(this.stats.maxTwist, Math.abs(this.twist));
    // twist after the mixer: hips turn toward travel, spine/chest counter-rotate so the torso holds the aim
    if (Math.abs(this.twist) > 1e-3) {
      this.root.updateMatrixWorld(true);
      this.yawBone(this.hips, this.twist);
      this.yawBone(this.spine, -this.twist * 0.55);
      this.yawBone(this.chest, -this.twist * 0.45);
    }
    // upper body turns toward the beam while the legs keep facing the travel direction (owner: no running backwards)
    const aimRel = d.aimYaw === undefined ? 0 : Math.max(-1.0, Math.min(1.0, Math.atan2(Math.sin(d.aimYaw - d.faceYaw), Math.cos(d.aimYaw - d.faceYaw))));
    const strikeRel = Math.max(-1.4, Math.min(1.4, Math.atan2(Math.sin(this.strikeYaw - d.faceYaw), Math.cos(this.strikeYaw - d.faceYaw))));
    const actRel = this.actionYawRel(d.faceYaw);
    // owner 2026-11-02 ('walking backwards when I walk forward… inconsistent'): auto-aimed shots at foes BEHIND him twisted
    // the chest/head up to 80° backwards while the legs ran forward, which reads as running backwards from the top-down
    // camera. While moving, the upper body only leans a little toward targets in front/to the side and never twists
    // toward one behind (the shot still leaves the hand and curves to its target). Standing still keeps the full twist.
    const moveK = Math.min(1, Math.max(0, (d.speed - 1.0) / 1.5)); // 0 standing → 1 walking+
    const lim = (r: number) => { if (moveK <= 0) return r; if (Math.abs(r) > 1.75) return r * (1 - moveK); const m = 1.4 - 0.95 * moveK; return Math.max(-m, Math.min(m, r)); };
    const twistGoal = d.firing || d.charging ? aimRel : this.strikeT > 0 ? strikeRel : actRel ?? 0;
    this.aimTwist += (lim(twistGoal) - this.aimTwist) * Math.min(1, dt * (this.strikeT > 0 || actRel !== null ? 20 : 12));
    if (Math.abs(this.aimTwist) > 1e-3) {
      this.root.updateMatrixWorld(true);
      this.yawBone(this.spine, this.aimTwist * 0.45);
      this.yawBone(this.chest, this.aimTwist * 0.55);
    }
    // ground contact (owner: 'feel planted, not floating'): lower/raise the whole body so the lowest ankle sits at its
    // rest height above the ground in every clip (walk, run, surge, flinch). Smoothed to avoid jitter.
    if (this.feet.length && !d.dead) {
      this.root.updateMatrixWorld(true);
      const baseY = this.root.position.y, inv = 1 / Math.max(1e-6, this.root.scale.y); // caller sets the ground position every frame
      let low = Infinity; for (const f of this.feet) { f.getWorldPosition(_footW); low = Math.min(low, _footW.y); }
      const rel = (low - this.root.position.y) * inv;
      if (Number.isNaN(this.restFootY)) this.restFootY = rel; // first frame = idle bind pose
      const want = -(rel - this.restFootY) * this.root.scale.y;
      this.plant += (Math.max(-0.36, Math.min(0.36, want)) - this.plant) * Math.min(1, dt * 18); // (±0.36: a deep fight-stance crouch raises the ankles ~30 cm)
      this.root.position.y = baseY + this.plant;
    }
  }
  // =====================================================================================================================
  // Skill action layer (?anims=new, owner approval pending; owner 2026-11-02: 'show it actually shooting the blasts out,
  // all of the blasts, and powering up. It should be more natural'). Gestures from assets/blender/aren_anims.py (act_*
  // clips in aren_anims_candidate.glb) layered over locomotion: upper-body clips leave the legs walking/running; the
  // full-body power-up/overdrive only take the legs while standing. Smoothstep envelopes in/out (no snapping), crossfade
  // on replacement, and one priority rule: power-up > overdrive > Nova Lance > charge holds > casts. Presentation only.
  // =====================================================================================================================
  /** Which hero GLB the game loads. Owner 2026-11-03 ('Ship most current usable aren model'): the Hy3D Aren
   *  (cel skin, anime face; Docs/DESIGN/HERO_HY3D_PIPELINE.md) is the default; ?hero=classic restores the previous body. */
  static heroFile(params: URLSearchParams): string {
    const q = params.get('hero');
    if (q === 'fighter') return 'aren_fighter.glb';
    if (q === 'classic' || GltfHero.ANIMS_NEW) return GltfHero.ANIMS_NEW || GltfHero.LIVE_ACTIONS.size ? 'aren_anims_candidate.glb' : 'aren.glb';
    return 'hero_hy3d_aren.glb';
  }
  static ANIMS_NEW = new URLSearchParams(typeof location !== 'undefined' ? location.search : '').get('anims') === 'new';
  /** actions that play WITHOUT ?anims=new (owner-approved ones; needs a model with their act_* clips). ?anims=bolt previews
   *  exactly this configuration with just the Spirit Bolt cast. To make the bolt cast live by default: ['bolt_r', 'bolt_l']. */
  // owner 2026-11-02: 'apply an animation to show it shooting the spirit bolts out of its hand' — Spirit Bolt cast live by
  // default (?anims=off disables it); every other action still needs ?anims=new until reviewed
  static LIVE_ACTIONS = new Set<string>(new URLSearchParams(typeof location !== 'undefined' ? location.search : '').get('anims') === 'off' ? [] : ['bolt_r', 'bolt_l']);
  /** per instance so the animation lab can show the current and the new behaviour side by side */
  actionsOn = GltfHero.ANIMS_NEW;
  private actClips = new Set<string>();
  private slots: ActSlot[] = [];
  private baseW = new Map<string, number>();
  private lay = { up: 0, lo: 0, hi: 0 };
  private handB: Record<'L' | 'R', THREE.Bone | null> = { L: null, R: null };
  private handFrame = -1;
  /** QA counters */
  actionStats = { played: 0, dropped: 0 };
  /** true when the action layer is on and the loaded model carries the act_* clips */
  get actionsLive(): boolean { return this.actionsOn && this.actClips.size > 0; }
  /** some actions can play (all with ?anims=new, else the LIVE_ACTIONS ones) */
  get actionsReady(): boolean { return this.actClips.size > 0 && (this.actionsOn || GltfHero.LIVE_ACTIONS.size > 0); }
  /** this action can play on this model */
  allows(id: string): boolean { return this.actClips.has(id) && (this.actionsOn || GltfHero.LIVE_ACTIONS.has(id)); }

  private initActions(mixer: THREE.AnimationMixer, clips: THREE.AnimationClip[], scene: THREE.Object3D): void {
    scene.traverse((o) => { if ((o as THREE.Bone).isBone && /^hand\.?[LR]$/.test(o.name)) this.handB[o.name.slice(-1) as 'L' | 'R'] = o as THREE.Bone; });
    for (const c of clips) {
      const id = c.name.startsWith('act_') ? c.name.slice(4) : '', spec = HERO_ACTIONS[id]; if (!spec) continue;
      for (const lower of spec.legs ? [false, true] : [false]) {
        const a = mixer.clipAction(splitClip(c, lower));
        a.setLoop(spec.hold ? THREE.LoopRepeat : THREE.LoopOnce, Infinity); a.clampWhenFinished = true;
        this.act.set(`${c.name}_${lower ? 'lo' : 'up'}`, a);
      }
      this.actClips.add(id);
    }
  }

  /** One-shot skill gesture toward world yaw `yaw` (rad). Returns false when a higher-priority action owns the body. */
  playAction(id: string, yaw?: number): boolean { return this.startAction(id, yaw); }
  /** Held action (charge / sweep): on while the skill holds, off releases it with its fade-out. */
  hold(id: string, on: boolean, yaw?: number): void {
    const s = this.slots.find((x) => x.id === id && x.out < 0);
    if (on) { if (s) { if (yaw !== undefined) s.yaw = yaw; } else this.startAction(id, yaw); } else if (s) this.release(s);
  }
  /** world yaw the standing hero should square up to while an aimed action plays (null = none) */
  actionFacing(): number | null {
    const s = this.slots.find((x) => x.out < 0 && x.yaw !== undefined);
    return s && s.yaw !== undefined ? s.yaw : null;
  }
  /** id of the action currently owning the upper body ('' = none) */
  get actionNow(): string { return this.slots.find((x) => x.out < 0)?.id ?? ''; }
  /** palm centre of the authored hand bone in world space (for muzzles / launch points) */
  handWorld(side: 'L' | 'R', out: THREE.Vector3): THREE.Vector3 {
    const b = this.handB[side]; if (!b) return out.copy(this.root.position);
    if (this.handFrame !== this.stats.frames) { this.handFrame = this.stats.frames; this.root.updateMatrixWorld(true); }
    return b.localToWorld(out.set(0, 0.075, 0));
  }

  /** tip of the extended index finger in world space (Finger Beam charge / muzzle, owner 2026-11-03). Hand-bone local
   *  (−0.006, 0.14, 0): measured from aren.glb's skin — the farthest vertex weighted to hand.R sits at y 0.137 in that bone's
   *  bind space (fingers.R's curled fingers reach 0.123), so this is just past the fingertip. */
  fingertipWorld(side: 'L' | 'R', out: THREE.Vector3): THREE.Vector3 {
    const b = this.handB[side]; if (!b) return out.copy(this.root.position);
    if (this.handFrame !== this.stats.frames) { this.handFrame = this.stats.frames; this.root.updateMatrixWorld(true); }
    return b.localToWorld(out.set(side === 'R' ? -0.006 : 0.006, 0.14, 0));
  }

  private startAction(id: string, yaw?: number): boolean {
    const spec = HERO_ACTIONS[id];
    if (!spec || !this.allows(id)) return false;
    const cur = this.slots.find((s) => s.out < 0);
    const lance = (this.baseW.get('beam_up') ?? 0) + (this.baseW.get('aim_up') ?? 0);
    const blocked = (spec.pri < 3 && lance > 0.5) || (spec.pri <= 2 && this.strikeT > 0)
      || (!!cur && (cur.spec.pri > spec.pri || (cur.spec.pri === spec.pri && cur.t < (cur.spec.minAge ?? 0.06))));
    if (blocked) { this.actionStats.dropped++; return false; }
    if (cur) this.release(cur, spec.fadeIn);
    this.slots = this.slots.filter((s) => s.id !== id); // the clip's action objects are shared: one slot per clip
    const up = this.act.get(`act_${id}_up`)!, lo = this.act.get(`act_${id}_lo`) ?? null;
    for (const a of [up, lo]) if (a) { a.reset(); a.timeScale = spec.rate ?? 1; a.setEffectiveWeight(0); a.play(); }
    this.slots.push({ id, spec, up, lo, t: 0, out: -1, outT: 0, w: 0, yaw, dur: up.getClip().duration / (spec.rate ?? 1) });
    this.actionStats.played++;
    return true;
  }
  private release(s: ActSlot, over = s.spec.fadeOut): void {
    if (s.out >= 0 && s.out <= over) return;
    s.out = over; s.outT = over;
  }
  private updateActions(dt: number, d: HeroDrive, walkW: number): void {
    if (!this.slots.length) { this.lay.up = this.lay.lo = this.lay.hi = 0; return; }
    const sm = (x: number) => (x <= 0 ? 0 : x >= 1 ? 1 : x * x * (3 - 2 * x));
    const lance = Math.min(1, (this.baseW.get('beam_up') ?? 0) + (this.baseW.get('aim_up') ?? 0));
    const strikeW = Math.min(1, this.strikeT / 0.03);
    let up = 0, lo = 0, hi = 0;
    for (const s of this.slots) {
      s.t += dt;
      if (d.dead) this.release(s, 0.1);
      if (s.out < 0 && !s.spec.hold && s.t >= s.dur - s.spec.fadeOut) this.release(s);
      if (s.out >= 0) s.out = Math.max(0, s.out - dt);
      let wt = sm(s.t / s.spec.fadeIn) * (s.out >= 0 ? sm(s.out / Math.max(1e-3, s.outT)) : 1);
      if (s.spec.pri < 3) wt *= 1 - lance;           // Nova Lance outranks holds and casts
      if (s.spec.pri <= 2) wt *= 1 - strikeW * 0.85; // an approved Rush Flurry / Pulse Nova blow plays through
      s.w = wt; up += wt; if (s.spec.pri > 3) hi += wt;
      if (s.lo) lo += wt * (1 - walkW);              // legs keep walking/running under a full-body action
    }
    const n = up > 1 ? 1 / up : 1;
    for (const s of this.slots) { s.up.setEffectiveWeight(s.w * n); s.lo?.setEffectiveWeight(s.w * n * (1 - walkW)); }
    for (const s of this.slots) if (s.out === 0) { s.up.stop(); s.lo?.stop(); }
    this.slots = this.slots.filter((s) => s.out !== 0);
    if (!this.slots.length) up = lo = hi = 0; // nothing left playing: locomotion gets the whole body back this frame
    this.lay.up = Math.min(1, up); this.lay.lo = Math.min(1, lo); this.lay.hi = Math.min(1, hi);
  }
  /** three.js' PropertyMixer skips writing a bone whose blended value did not change since the last frame, so while an
   *  action holds the torso still the post-mixer yawBone() twists would pile up frame after frame. Keep the mixer's own
   *  torso result and put it back before the next mixer update (action layer only; the live path is unchanged). */
  private torsoQ: Array<[THREE.Bone, THREE.Quaternion]> = [];
  private restoreTorso(): void { if (this.actionsReady) for (const [b, q] of this.torsoQ) b.quaternion.copy(q); }
  private saveTorso(): void {
    if (!this.actionsReady) return;
    if (!this.torsoQ.length) for (const b of [this.hips, this.spine, this.chest]) if (b) this.torsoQ.push([b, new THREE.Quaternion()]);
    for (const [b, q] of this.torsoQ) q.copy(b.quaternion);
  }
  /** locomotion / lance weight multiplier left over by the action layer */
  private layerScale(n: string): number {
    if (n === 'beam_up' || n === 'aim_up') return 1 - this.lay.hi;
    return n.endsWith('_lo') ? 1 - this.lay.lo : 1 - this.lay.up;
  }
  /** upper-body twist toward the playing action's target, relative to the body facing (null = no aimed action) */
  private actionYawRel(faceYaw: number): number | null {
    let best: ActSlot | null = null;
    for (const s of this.slots) if (s.yaw !== undefined && s.w > 0.05 && (!best || s.w > best.w)) best = s;
    if (!best || best.yaw === undefined) return null;
    const r = Math.atan2(Math.sin(best.yaw - faceYaw), Math.cos(best.yaw - faceYaw));
    return Math.max(-1.4, Math.min(1.4, r));
  }
}

interface ActSpec {
  /** priority: 5 power-up, 4 overdrive, (3 = Nova Lance, driven by beam/aim), 2 charge holds + their releases, 1 casts */
  pri: number; fadeIn: number; fadeOut: number;
  /** playback rate */ rate?: number;
  /** full-body clip: also drives the legs while standing */ legs?: boolean;
  /** looping hold (released by hold(id, false)) */ hold?: boolean;
  /** an equal-priority action may replace this one only after it has played this long (s) */ minAge?: number;
}
interface ActSlot { id: string; spec: ActSpec; up: THREE.AnimationAction; lo: THREE.AnimationAction | null; t: number; out: number; outT: number; w: number; yaw?: number; dur: number }
/** Skill actions (clip = act_<id> in the GLB). Casts are 0.30-0.47 s so rapid fire never stacks up. */
export const HERO_ACTIONS: Record<string, ActSpec> = {
  bolt_r: { pri: 1, fadeIn: 0.035, fadeOut: 0.12, minAge: 0.05 }, bolt_l: { pri: 1, fadeIn: 0.035, fadeOut: 0.12, minAge: 0.05 },
  skyfall: { pri: 1, fadeIn: 0.05, fadeOut: 0.16, minAge: 0.12 },
  breaker: { pri: 1, fadeIn: 0.04, fadeOut: 0.14, minAge: 0.1 }, bore: { pri: 1, fadeIn: 0.04, fadeOut: 0.12, minAge: 0.08 },
  disc: { pri: 1, fadeIn: 0.04, fadeOut: 0.14, minAge: 0.1 }, arc: { pri: 1, fadeIn: 0.04, fadeOut: 0.12, minAge: 0.08 },
  glare: { pri: 1, fadeIn: 0.04, fadeOut: 0.15, minAge: 0.12 }, lure: { pri: 1, fadeIn: 0.05, fadeOut: 0.14, minAge: 0.12 },
  mortar_charge: { pri: 2, fadeIn: 0.1, fadeOut: 0.1, hold: true }, mortar_push: { pri: 2, fadeIn: 0.04, fadeOut: 0.16, minAge: 0.1 },
  zenith_hold: { pri: 2, fadeIn: 0.18, fadeOut: 0.12, hold: true }, zenith_throw: { pri: 2, fadeIn: 0.05, fadeOut: 0.16, minAge: 0.12 },
  rift: { pri: 2, fadeIn: 0.08, fadeOut: 0.16, hold: true },
  overdrive: { pri: 4, fadeIn: 0.05, fadeOut: 0.16, legs: true, minAge: 0.2 },
  powerup: { pri: 5, fadeIn: 0.06, fadeOut: 0.22, legs: true, minAge: 0.3 },
};
